import React, { useState } from 'react';
import { Box, Typography, Button, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { makeStyles } from 'tss-react/mui';
import { VendorAccountDetail } from '../types';
import {
  getAchAccountNumber,
  getOriginalAccountNumberFromHistory,
} from '../api/vendorApi/vendorAccountsApi';

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    minWidth: 550,
    maxWidth: 700,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    padding: 0,
    boxShadow: theme.shadows[8],
    fontFamily: 'Inter',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.primary.light,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: '24px 32px 12px 32px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerText: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '24px',
    color: theme.palette.common.white,
  },
  headerSub: {
    fontFamily: 'Inter',
    fontSize: '15px',
    color: theme.palette.text.disabled,
    fontWeight: 400,
  },
  closeIcon: {
    color: theme.palette.common.white,
    '&:hover': { opacity: 0.7 },
    fontSize: 22,
  },
  contentContainer: {
    background: theme.palette.primary.light,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 32,
    justifyContent: 'center',
    alignItems: 'start',
    padding: '0 32px',
  },
  columnHeader: {
    background: theme.palette.primary.light,
    paddingBottom: 8,
    paddingTop: 16,
  },
  columnTitle: {
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: '20px',
    color: theme.palette.common.white,
  },
  columnSub: {
    fontFamily: 'Inter',
    fontSize: '13px',
    color: theme.palette.text.disabled,
    fontWeight: 400,
  },
  contentWrapper: {
    background: theme.palette.primary.light,
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 32px',
    paddingTop: 16,
    paddingBottom: 16,
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '3px',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' },
    },
  },
  scrollContent: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 32,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  section: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: '20px 24px',
    marginBottom: 8,
    border: '2px solid',
    minHeight: '60px',
  },
  sectionSmall: {
    minHeight: '80px',
  },
  sectionPrev: {
    borderColor: theme.palette.warning.main,
  },
  sectionCurr: {
    borderColor: theme.palette.success.light,
  },
  sectionTitle: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '16px',
    color: theme.palette.common.white,
    marginBottom: '16px',
  },
  label: {
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 500,
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  value: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.common.white,
    fontWeight: 500,
    marginBottom: '8px',
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.05)',
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    padding: '24px 0 16px 0',
    marginTop: 8,
  },
  closeButton: {
    height: '48px',
    borderRadius: '12px',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '16px',
    minWidth: '120px',
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.info.main,
      opacity: 0.9,
    },
  },
  accountNumberContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  eyeIcon: {
    color: theme.palette.info.main,
    cursor: 'pointer',
    fontSize: '16px',
    minWidth: '16px',
    minHeight: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      opacity: 0.7,
    },
  },
  accountNumberText: {
    flex: 1,
  },
}));

interface VendorDetails {
  mailingAddress: string;
  participationAddress: string;
  accountType: string;
  accountNumber: string;
  routingNumber: string;
  mailingPhoneNumber?: string;
  email?: string;
  defaultPaymentMethod?: string;
  contactName?: string;
}

interface ViewVendorChangesPopupProps {
  open: boolean;
  onClose: () => void;
  data: VendorAccountDetail;
}

const ViewVendorChangesPopup: React.FC<ViewVendorChangesPopupProps> = ({
  open,
  onClose,
  data
}) => {
  const { classes } = useStyles();
  const [unmaskedAccountNumbers, setUnmaskedAccountNumbers] = useState<{
    previous?: string;
    current?: string;
  }>({});
  const [isLoadingAccountNumber, setIsLoadingAccountNumber] = useState<{
    previous?: boolean;
    current?: boolean;
  }>({});

  const changes = data?.history?.modificationHistory?.changedFields || [];
  const hasChanged = (field: string) =>
    changes.some((c) => c.fieldName === field);

  const isOriginalAccountNumberEmpty = () => {
    const accountNumberChange = changes.find((c) => c.fieldName === 'accountNumber' && c.isAccountNumber);
    return accountNumberChange?.originalValue === false;
  };

  const handleToggleAccountNumber = async (type: 'previous' | 'current') => {
    // If already unmasked, toggle back to masked
    if (unmaskedAccountNumbers[type]) {
      setUnmaskedAccountNumbers((prev) => ({
        ...prev,
        [type]: undefined,
      }));
      return;
    }

    // If loading, do nothing
    if (isLoadingAccountNumber[type]) {
      return;
    }

    // If not loaded yet, fetch from API
    if (!data?.id) {
      return;
    }

    setIsLoadingAccountNumber((prev) => ({ ...prev, [type]: true }));

    try {
      const response =
        type === 'previous'
          ? await getOriginalAccountNumberFromHistory(data.id)
          : await getAchAccountNumber(data.id);

      if (response.data?.accountNumber) {
        setUnmaskedAccountNumbers((prev) => ({
          ...prev,
          [type]: response.data.accountNumber,
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${type} account number:`, error);
    } finally {
      setIsLoadingAccountNumber((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleClose = () => {
    // Reset unmasked account numbers for security
    setUnmaskedAccountNumbers({});
    setIsLoadingAccountNumber({});
    onClose();
  };

  const prev: VendorDetails = {
    mailingAddress: '',
    accountType: '',
    accountNumber: '',
    routingNumber: '',
    mailingPhoneNumber: '',
    participationAddress: '',
    email: '',
    defaultPaymentMethod: '',
    contactName: '',
  };

  const curr: VendorDetails = {
    mailingAddress: '',
    accountType: '',
    accountNumber: '',
    routingNumber: '',
    mailingPhoneNumber: '',
    participationAddress: '',
    email: '',
    defaultPaymentMethod: '',
    contactName: '',
  };

  changes.forEach((item) => {
    if (item.fieldName === 'accountNumber' && item.isAccountNumber) {
      // Account numbers are handled directly in the display components
      // No need to set them here as they're managed by the toggle state
    } else if (item.fieldName in prev) {
      (prev as unknown as Record<string, string>)[item.fieldName] = item.originalValue;
      (curr as unknown as Record<string, string>)[item.fieldName] = item.updatedValue;
    }
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby='view-vendor-changes-title'
      fullWidth
    >
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.headerText}>Vendor Details Confirmation</Typography>
          <Typography className={classes.headerSub}>Review changes and confirm updates</Typography>
        </Box>
        <IconButton onClick={handleClose} className={classes.closeIcon} size='large'>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Column headers */}
      <Box className={classes.contentContainer}>
        <Box className={classes.columnHeader}>
          <Typography className={classes.columnTitle}>Previous Details</Typography>
          <Typography className={classes.columnSub}>Original information</Typography>
        </Box>

        <Box className={classes.columnHeader}>
          <Typography className={classes.columnTitle}>Current Details</Typography>
          <Typography className={classes.columnSub}>Updated information</Typography>
        </Box>
      </Box>

      {/* Scrollable content */}
      <Box className={classes.contentWrapper}>
        <Box className={classes.scrollContent}>

          {/* Default Payment Method Section  */}
          {(hasChanged('defaultPaymentMethod')) && (
            <>
              {/* Previous Default Payment Method */}
              <Box className={`${classes.section} ${classes.sectionPrev}`}>
                <Typography className={classes.sectionTitle}>Default Payment Method</Typography>
                {hasChanged('defaultPaymentMethod') && (

                  <>                    
                    <Typography className={classes.value}>{prev.defaultPaymentMethod || '-'}</Typography>
                  </>
                )}
              </Box>
              {/* Current Default Payment Method */}
              <Box className={`${classes.section} ${classes.sectionCurr}`}>
                <Typography className={classes.sectionTitle}>Default Payment Method</Typography>
                {hasChanged('defaultPaymentMethod') && (
                  <>                    
                    <Typography className={classes.value}>{curr.defaultPaymentMethod || '-'}</Typography>
                  </>
                )}
              </Box>
            </>
          )}

          {/* Contact Name Section */}
          {(hasChanged('contactName') || hasChanged('email')) && (
            <>
              <Box className={`${classes.section} ${classes.sectionSmall} ${classes.sectionPrev}`}>
                <Typography className={classes.sectionTitle}>Contact Information</Typography>
                {hasChanged('contactName') && (
                  <>
                    <Typography className={classes.label}>Person to contact</Typography>
                    <Typography className={classes.value}>{prev.contactName || '-'}</Typography>
                  </>
                )}
                {hasChanged('email') && (
                  <>
                    <Typography className={classes.label}>Email</Typography>
                    <Typography className={classes.value}>{prev.email || '-'}</Typography>
                  </>
                )}
                </Box>
              <Box className={`${classes.section} ${classes.sectionSmall} ${classes.sectionCurr}`}>
                <Typography className={classes.sectionTitle}>Contact Information</Typography>
                {hasChanged('contactName') && (
                  <>
                    <Typography className={classes.label}>Person to contact</Typography>
                    <Typography className={classes.value}>{curr.contactName || '-'}</Typography>
                  </>
                )}
                {hasChanged('email') && (
                  <>
                    <Typography className={classes.label}>Email</Typography>
                    <Typography className={classes.value}>{curr.email || '-'}</Typography>
                  </>
                )}
                </Box>
            </>
          )}

          {/* Mailing Address Section */}
          {(hasChanged('mailingAddress') || hasChanged('mailingPhoneNumber')) && (
            <>
              {/* Previous Mailing Address */}
              <Box className={`${classes.section} ${classes.sectionPrev}`}>
                <Typography className={classes.sectionTitle}>Mailing Address</Typography>
                {(hasChanged('mailingAddress')) && (
                  <>
                    {/* <Typography className={classes.label}>Address</Typography> */}
                    <Typography className={classes.value}>{prev.mailingAddress || '-'}</Typography>
                  </>
                )}
                {hasChanged('mailingPhoneNumber') && (
                  <>
                    <Typography className={classes.label}>Phone Number</Typography>
                    <Typography className={classes.value}>{prev.mailingPhoneNumber || '-'}</Typography>
                  </>
                )}
              </Box>

              {/* Current Mailing Address */}
              <Box className={`${classes.section} ${classes.sectionCurr}`}>
                <Typography className={classes.sectionTitle}>Mailing Address</Typography>
                {(hasChanged('mailingAddress')) && (
                  <>
                    {/* <Typography className={classes.label}>Address</Typography> */}
                    <Typography className={classes.value}>{curr.mailingAddress || '-'}</Typography>
                  </>
                )}
                {hasChanged('mailingPhoneNumber') && (
                  <>
                    <Typography className={classes.label}>Phone Number</Typography>
                    <Typography className={classes.value}>{curr.mailingPhoneNumber || '-'}</Typography>
                  </>
                )}
              </Box>
            </>
          )}

          {/* Banking Details Section */}
          {(hasChanged('accountType') ||
            hasChanged('accountNumber') ||
            hasChanged('routingNumber')) && (
              <>
                {/* Previous Banking Details */}
                <Box className={`${classes.section} ${classes.sectionPrev}`}>
                  <Typography className={classes.sectionTitle}>Banking Details (ACH)</Typography>

                  {hasChanged('accountType') && (
                    <>
                      <Typography className={classes.label}>Account Type</Typography>
                      <Typography className={classes.value}>{prev.accountType || '-'}</Typography>
                    </>
                  )}

                  {hasChanged('accountNumber') && (
                    <>
                      <Typography className={classes.label}>Account Number</Typography>
                      {isOriginalAccountNumberEmpty() ? (
                        <Typography className={classes.value}>-</Typography>
                      ) : (
                        <Box className={classes.accountNumberContainer}>
                          <Typography className={`${classes.value} ${classes.accountNumberText}`}>
                            {unmaskedAccountNumbers.previous || '************'}
                          </Typography>
                          {unmaskedAccountNumbers.previous ? (
                            <VisibilityOffIcon
                              className={classes.eyeIcon}
                              onClick={() => handleToggleAccountNumber('previous')}
                              style={{
                                opacity: isLoadingAccountNumber.previous ? 0.5 : 1,
                                cursor: isLoadingAccountNumber.previous ? 'wait' : 'pointer',
                              }}
                            />
                          ) : (
                            <VisibilityIcon
                              className={classes.eyeIcon}
                              onClick={() => handleToggleAccountNumber('previous')}
                              style={{
                                opacity: isLoadingAccountNumber.previous ? 0.5 : 1,
                                cursor: isLoadingAccountNumber.previous ? 'wait' : 'pointer',
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  )}

                  {hasChanged('routingNumber') && (
                    <>
                      <Typography className={classes.label}>Routing Number</Typography>
                      <Typography className={classes.value}>{prev.routingNumber || '-'}</Typography>
                    </>
                  )}
                </Box>

                {/* Current Banking Details */}
                <Box className={`${classes.section} ${classes.sectionCurr}`}>
                  <Typography className={classes.sectionTitle}>Banking Details (ACH)</Typography>

                  {hasChanged('accountType') && (
                    <>
                      <Typography className={classes.label}>Account Type</Typography>
                      <Typography className={classes.value}>{curr.accountType || '-'}</Typography>
                    </>
                  )}

                  {hasChanged('accountNumber') && (
                    <>
                      <Typography className={classes.label}>Account Number</Typography>
                      <Box className={classes.accountNumberContainer}>
                        <Typography className={`${classes.value} ${classes.accountNumberText}`}>
                          {unmaskedAccountNumbers.current || '************'}
                        </Typography>
                        {unmaskedAccountNumbers.current ? (
                          <VisibilityOffIcon
                            className={classes.eyeIcon}
                            onClick={() => handleToggleAccountNumber('current')}
                            style={{
                              opacity: isLoadingAccountNumber.current ? 0.5 : 1,
                              cursor: isLoadingAccountNumber.current ? 'wait' : 'pointer',
                            }}
                          />
                        ) : (
                          <VisibilityIcon
                            className={classes.eyeIcon}
                            onClick={() => handleToggleAccountNumber('current')}
                            style={{
                              opacity: isLoadingAccountNumber.current ? 0.5 : 1,
                              cursor: isLoadingAccountNumber.current ? 'wait' : 'pointer',
                            }}
                          />
                        )}
                      </Box>
                    </>
                  )}

                  {hasChanged('routingNumber') && (
                    <>
                      <Typography className={classes.label}>Routing Number</Typography>
                      <Typography className={classes.value}>{curr.routingNumber || '-'}</Typography>
                    </>
                  )}
                </Box>
              </>
            )}

          {/* Participation Address Section */}
          {(hasChanged('participationAddress')) && (
            <>
              {/* Previous Participation Address */}
              <Box className={`${classes.section} ${classes.sectionPrev}`}>
                <Typography className={classes.sectionTitle}>Participation Address</Typography>
                {(hasChanged('participationAddress')) && (
                  <>
                    {/* <Typography className={classes.label}>Address</Typography> */}
                    <Typography className={classes.value}>{prev.participationAddress || '-'}</Typography>
                  </>
                )}
              </Box>

              {/* Current Participation Address */}
              <Box className={`${classes.section} ${classes.sectionCurr}`}>
                <Typography className={classes.sectionTitle}>Participation Address</Typography>
                {(hasChanged('participationAddress')) && (
                  <>
                    {/* <Typography className={classes.label}>Address</Typography> */}
                    <Typography className={classes.value}>{curr.participationAddress || '-'}</Typography>
                  </>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Close Button */}
      <Box className={classes.actions}>
        <Button onClick={handleClose} className={classes.closeButton} variant='contained'>
          CLOSE
        </Button>
      </Box>
    </Dialog>
  );
};

export default ViewVendorChangesPopup;
